import type { NotificationItem, Page } from '@greensm/api-client';
import { useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useServices } from '@/app/services';
import { dedupeById } from '@/features/trip-history/hooks';
import { qk } from '@/shared/lib/queryKeys';

export type NotifFilter = 'all' | 'promo';

type Feed = InfiniteData<Page<NotificationItem>, string | undefined>;

export function useNotificationFeed(filter: NotifFilter) {
  const { api, v3, v3Enabled } = useServices();
  const q = useInfiniteQuery({
    queryKey: qk.notifications(filter),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => v3Enabled
      ? v3.customer.notifications({ cursor: pageParam, category: filter === 'promo' ? 'PROMO' : undefined }, signal)
      : api.notifications.list({ cursor: pageParam, category: filter === 'promo' ? 'PROMO' : undefined }, signal),
    refetchInterval: 30_000,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = useMemo(() => dedupeById(q.data?.pages.flatMap((p) => p.items) ?? []), [q.data]);
  return { ...q, items, unread: items.some((n) => !n.seen) };
}

/**
 * Đánh dấu đã xem – idempotent nên GỬI LẠI AN TOÀN: cập nhật lạc quan, tự thử lại 3 lần khi lỗi mạng,
 * hết lần thử mà vẫn lỗi thì hoàn tác để lần mở sau gửi lại.
 */
export function useMarkSeen() {
  const { api, v3, v3Enabled } = useServices();
  const qc = useQueryClient();

  const setSeen = (id: string, seen: boolean) => {
    qc.setQueriesData<Feed>({ queryKey: ['notifications'] }, (old) =>
      old && { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.map((n) => (n.id === id ? { ...n, seen } : n)) })) });
  };

  return useMutation({
    mutationFn: async (id: string) => { if (v3Enabled) await v3.customer.markNotificationSeen(id); else await api.notifications.markSeen(id); },
    retry: 3,
    retryDelay: (n) => Math.min(500 * 2 ** n, 4000),
    onMutate: (id) => setSeen(id, true),
    onError: (_e, id) => setSeen(id, false),
  });
}
